import api from '@/lib/axios';

export interface Settings {
    _id: string;
    general: {
        siteName: string;
        siteDescription?: string;
        logo?: {
            url: string;
            publicId: string;
        };
        favicon?: {
            url: string;
            publicId: string;
        };
        contactEmail: string;
        contactPhone?: string;
        address?: string;
    };
    seo: {
        metaTitle?: string;
        metaDescription?: string;
        metaKeywords?: string[];
        ogImage?: {
            url: string;
            publicId: string;
        };
    };
    social: {
        facebook?: string;
        instagram?: string;
        twitter?: string;
        youtube?: string;
        tiktok?: string;
        zalo?: string;
    };
    payment: {
        methods: {
            cod: { enabled: boolean };
            bankTransfer: {
                enabled: boolean;
                bankName?: string;
                accountNumber?: string;
                accountHolder?: string;
                branch?: string;
                qrCode?: { url: string; publicId?: string };
            };
            vnpay: { enabled: boolean; merchantId?: string; secretKey?: string };
            momo: { enabled: boolean; partnerCode?: string; secretKey?: string };
        };
    };
    shipping: {
        methods: {
            standard: { enabled: boolean; price: number; freeShippingThreshold?: number };
            express: { enabled: boolean; price: number };
            international: { enabled: boolean; price: number };
        };
    };
    email: {
        smtp?: {
            host?: string;
            port?: number;
            secure?: boolean;
            user?: string;
            password?: string;
            from?: string;
        };
        templates?: {
            orderConfirmation?: string;
            orderShipped?: string;
            orderDelivered?: string;
        };
    };
    maintenance: {
        enabled: boolean;
        message?: string;
    };
    features?: Array<{
        icon: string;
        title: string;
        description: string;
        enabled?: boolean;
        order?: number;
    }>;
    createdAt: string;
    updatedAt: string;
}

export interface SettingsInput {
    general?: {
        siteName?: string;
        siteDescription?: string;
        logo?: {
            url: string;
            publicId: string;
        };
        favicon?: {
            url: string;
            publicId: string;
        };
        contactEmail?: string;
        contactPhone?: string;
        address?: string;
    };
    seo?: {
        metaTitle?: string;
        metaDescription?: string;
        metaKeywords?: string[];
        ogImage?: {
            url: string;
            publicId: string;
        };
    };
    social?: {
        facebook?: string;
        instagram?: string;
        twitter?: string;
        youtube?: string;
        tiktok?: string;
        zalo?: string;
    };
    payment?: {
        methods?: {
            cod?: { enabled?: boolean };
            bankTransfer?: {
                enabled?: boolean;
                bankName?: string;
                accountNumber?: string;
                accountHolder?: string;
                branch?: string;
                qrCode?: { url: string; publicId?: string };
            };
            vnpay?: { enabled?: boolean; merchantId?: string; secretKey?: string };
            momo?: { enabled?: boolean; partnerCode?: string; secretKey?: string };
        };
    };
    shipping?: {
        methods?: {
            standard?: { enabled?: boolean; price?: number; freeShippingThreshold?: number };
            express?: { enabled?: boolean; price?: number };
            international?: { enabled?: boolean; price?: number };
        };
    };
    email?: {
        smtp?: {
            host?: string;
            port?: number;
            secure?: boolean;
            user?: string;
            password?: string;
            from?: string;
        };
        templates?: {
            orderConfirmation?: string;
            orderShipped?: string;
            orderDelivered?: string;
        };
    };
    maintenance?: {
        enabled?: boolean;
        message?: string;
    };
    features?: Array<{
        icon?: string;
        title?: string;
        description?: string;
        enabled?: boolean;
        order?: number;
    }>;
}

export interface SettingsResponse {
    success: boolean;
    message: string;
    data: Settings;
}

export const settingsApi = {
    // Get public settings (no auth required)
    getPublicSettings: async (): Promise<SettingsResponse> => {
        const response = await api.get<SettingsResponse>('/settings');
        return response.data;
    },

    // Get settings (admin only)
    getSettings: async (): Promise<SettingsResponse> => {
        const response = await api.get<SettingsResponse>('/settings/admin');
        return response.data;
    },

    // Update settings (admin only)
    updateSettings: async (data: SettingsInput): Promise<SettingsResponse> => {
        const response = await api.put<SettingsResponse>('/settings/admin', data);
        return response.data;
    },
};
